import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {CoreDI, inject, LanguageService} from '@gongxigames/framework/core';
import {GameConfig} from '@gongxigames/framework/slot/types';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic/LogicEvents';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels';
import {GenericPopupEvents} from '@gongxigames/framework/slot/modules/genericPopup';
import {PickBonusEvents} from '@gongxigames/framework/slot/modules/pick';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow/FlowModuleEvents';
import {BuyBonusPopUpEvents} from '@gongxigames/framework/slot/modules/buyBonus';
import {WinPresentationEvents} from '@gongxigames/framework/slot/modules/winpresentation';
import {FlowBasicState} from '@gongxigames/framework/slot/modules/flow/states/FlowBasicState';
import {FlowFreeSpinsState} from '@gongxigames/framework/slot/modules/flow/states/FlowFreeSpinsState';
import {FlowPickState} from '@gongxigames/framework/slot/modules/flow/states/FlowPickState';
import {FlowFreeGamesPickState} from '@gongxigames/framework/slot/modules/flow/states/FlowFreeGamesPickState';
import {FlowRestoreState} from '@gongxigames/framework/slot/modules/flow/states/FlowRestoreState';
import {FlowRespinState} from '@gongxigames/framework/slot/modules/flow/states/FlowRespinState';
import {FlowFirecatcherState} from '@gongxigames/framework/slot/modules/flow/states/FlowFirecatcherState';
import {FlowFreeSpinsFirecatcherState} from '@gongxigames/framework/slot/modules/flow/states/FlowFreeSpinsFirecatcherState';
import {FlowRespinInFreespinsState} from '@gongxigames/framework/slot/modules/flow/states/FlowRespinInFreespinsState';
import {FlowBuyBonusState} from '@gongxigames/framework/slot/modules/flow/states/FlowBuyBonusState';
import {UIDisableReason} from '@gongxigames/framework/slot/modules/keypad';

export class FlowModule extends Module<null, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('FlowModule');

    public static readonly RESTORE_STATE: string = 'restore';
    public static readonly BASIC_STATE: string = 'basic';
    public static readonly FREE_SPINS_STATE: string = 'freespins';
    public static readonly PICK_STATE: string = 'pick';
    public static readonly FREE_GAMES_PICK_STATE: string = 'free_games_pick';
    public static readonly FREESPINS_RESPIN_STATE: string = 'respininfreespins';
    public static readonly RESPIN_BASIC_STATE: string = 'respin';
    public static readonly FIRECATCHER_STATE: string = 'firecatcher';
    public static readonly FREESPINS_FIRECATCHER_STATE: string = 'freespinsfirecatcher';
    public static readonly BUY_BONUS_STATE: string = 'buybonus';

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    protected _isRestore = false;

    public restore(): void {
        if (this.logic.nextAction !== Action.SPIN) {
            this.gotoState(FlowModule.RESTORE_STATE);
            this._isRestore = true;
        }
    }

    protected onResourcesLoaded(): void {
        this.currentState.handle('General.ResourcesLoading.LOADED_ALL_RESOURCES');
    }

    protected setupEvents(): void {
        this.onCurrentStateHandle('General.Game.STARTED');
        this.onCurrentStateHandle(ReelsEvents.REELS_SPINNING_STARTED);
        this.onCurrentStateHandle(ReelsEvents.SPIN_ENDED);
        this.onCurrentStateHandle(GenericPopupEvents.POPUP_CLOSED);
        this.onCurrentStateHandle(GenericPopupEvents.FADE_OUT_STARTED);
        this.onCurrentStateHandle(LogicEvents.PICK_RESPONSE);
        this.onCurrentStateHandle(PickBonusEvents.STARTED);
        this.onCurrentStateHandle(PickBonusEvents.FINISHED);
        this.onCurrentStateHandle(FlowModuleEvents.CAN_SPIN);
        this.onCurrentStateHandle(FlowModuleEvents.RESPIN_WIN);
        this.onCurrentStateHandle('General.Flow.WAIT_EVENT_BEFORE_RESTORE_GAME');
        this.onCurrentStateHandle(BuyBonusPopUpEvents.BUY_BUTTON_CLICKED);

        // we should handle this event last
        this.onCurrentStateHandle(WinPresentationEvents.PAY_ANIMATION_STARTED, -1);

        this.on(LogicEvents.SPIN_RESPONSE, this.onSpinResponse);
        this.on(LogicEvents.FREE_SPIN_RESPONSE, this.onFreeSpinResponse);
        this.on(ReelsEvents.SPIN_ENDED, this.onAllReelsStopped);
        this.on('General.ResourcesLoading.LOADED_ALL_RESOURCES', this.onResourcesLoaded);
    }

    protected setupViews(): void {
    }

    protected setupStates(): void {
        this.addState(new FlowBasicState(this, FlowModule.BASIC_STATE));
        this.addState(new FlowFreeSpinsState(this, FlowModule.FREE_SPINS_STATE));
        this.addState(new FlowPickState(this, FlowModule.PICK_STATE));
        this.addState(new FlowFreeGamesPickState(this, FlowModule.FREE_GAMES_PICK_STATE));
        this.addState(new FlowRestoreState(this, FlowModule.RESTORE_STATE));
        this.addState(new FlowRespinState(this, FlowModule.RESPIN_BASIC_STATE));
        this.addState(new FlowFirecatcherState(this, FlowModule.FIRECATCHER_STATE));
        this.addState(new FlowFreeSpinsFirecatcherState(this, FlowModule.FREESPINS_FIRECATCHER_STATE));
        this.addState(new FlowRespinInFreespinsState(this, FlowModule.FREESPINS_RESPIN_STATE));
        this.addState(new FlowBuyBonusState(this, FlowModule.BUY_BONUS_STATE));

        this.gotoState(FlowModule.BASIC_STATE);
    }

    protected onSpinResponse(): void {
        switch (this.logic.nextAction) {
            case Action.SPIN: {
                this.eventManager.emit(FlowModuleEvents.NON_BLOCKING_FLOW);
                break;
            }

            case Action.FREESPIN: {
                this.eventManager.emit(FlowModuleEvents.BLOCKING_FLOW);
                break;
            }

            case Action.SELECT:
            case Action.PICK: {
                this.eventManager.emit(FlowModuleEvents.BLOCKING_FLOW);
                break;
            }

            default:
                break;
        }
    }

    protected onFreeSpinResponse(): void {
        switch (this.logic.nextAction) {
            case Action.RESPIN:
            case Action.FREESPIN: {
                if (this.logic.isRetriggerWon()) {
                    this.eventManager.emit(FlowModuleEvents.BLOCKING_FLOW);
                } else {
                    this.eventManager.emit(FlowModuleEvents.NON_BLOCKING_FLOW);
                }
                break;
            }
            case Action.SPIN: {
                this.eventManager.emit(FlowModuleEvents.NON_BLOCKING_FLOW);
                break;
            }

            case Action.SELECT:
            case Action.PICK: {
                this.eventManager.emit(FlowModuleEvents.BLOCKING_FLOW);
                break;
            }

            default:
                break;
        }
    }

    protected onAllReelsStopped(): void {
        const {nextAction, previousAction} = this.logic;
        const isNotSameAction = nextAction !== previousAction;
        const isNotAfterRespin = previousAction !== Action.RESPIN;

        if (this.logic.isRetriggerWon() ||
            nextAction === Action.FREESPIN && isNotSameAction && isNotAfterRespin ||
            nextAction === Action.RESPIN && isNotSameAction ||
            nextAction === Action.SELECT && isNotAfterRespin ||
            nextAction === Action.PICK && isNotAfterRespin) {
            this.eventManager.emit('General.UI.DISABLE', UIDisableReason.TRIGGERING_FEATURE);
        }
    }

    public get isRestore(): boolean {
        return this._isRestore;
    }

    public set isRestore(isRestore: boolean) {
        this._isRestore = isRestore;
    }

    public get languageService(): LanguageService {
        return this._languageService;
    }

    public get getRubyCoinSymbolId(): number | undefined {
        if (this._gameConfig.isRubyCoinsGame) {
            return this._gameConfig.reelsConfig.rubySymbol;
        }

        return undefined;
    }
}
